import { useEffect } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutInfo, siteInfo } from '@/mocks/data';

export default function AboutPage() {
  useEffect(() => {
    document.title = `关于我们 - ${siteInfo.name} | 必赢亚洲官网`;
  }, []);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于${siteInfo.name}`,
    description: aboutInfo.intro,
    url: `${siteInfo.url}/about`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
    },
  };

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: '关于我们' },
  ];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <main className="min-h-screen bg-background-50">
        <div className="pt-16 md:pt-18">
          <Breadcrumb items={breadcrumbItems} />

          <section className="w-full px-4 md:px-6 py-10 md:py-16">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12 md:mb-16">
                <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
                  关于必赢亚洲
                </h1>
                <p className="text-sm md:text-base text-foreground-500 max-w-3xl mx-auto leading-relaxed">
                  {aboutInfo.intro}
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 mb-14 md:mb-20">
                <div className="bg-background-100 rounded-2xl p-8 md:p-10">
                  <div className="w-12 h-12 bg-primary-50 rounded-xl flex items-center justify-center mb-4">
                    <i className="ri-flag-line text-2xl text-primary-500"></i>
                  </div>
                  <h2 className="text-xl font-bold text-foreground-950 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
                    我们的使命
                  </h2>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    {aboutInfo.mission}
                  </p>
                </div>

                <div className="bg-background-100 rounded-2xl p-8 md:p-10">
                  <div className="w-12 h-12 bg-accent-50 rounded-xl flex items-center justify-center mb-4">
                    <i className="ri-eye-line text-2xl text-accent-500"></i>
                  </div>
                  <h2 className="text-xl font-bold text-foreground-950 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
                    我们的愿景
                  </h2>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    {aboutInfo.vision}
                  </p>
                </div>
              </div>

              <div className="mb-14 md:mb-20">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8 text-center" style={{ fontFamily: "var(--font-heading)" }}>
                  发展历程
                </h2>
                <div className="relative">
                  <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 md:-translate-x-px"></div>
                  <div className="flex flex-col gap-6">
                    {aboutInfo.milestones.map((ms, index) => (
                      <div key={index} className={`relative flex items-start gap-6 md:gap-0 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                        <div className="md:w-1/2 flex items-center md:justify-end gap-4 md:pr-10">
                          <div className={`${index % 2 === 0 ? '' : 'md:pl-10 md:text-left'} text-right`}>
                            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-bold mb-1">
                              {ms.year}
                            </span>
                            <p className="text-sm text-foreground-700 leading-relaxed">{ms.event}</p>
                          </div>
                        </div>
                        <div className="absolute left-3.5 md:left-1/2 w-3 h-3 rounded-full bg-primary-500 border-2 border-background-50 md:-translate-x-1.5 mt-1.5 z-10"></div>
                        <div className="md:w-1/2 md:pl-10"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mb-14 md:mb-20">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8 text-center" style={{ fontFamily: "var(--font-heading)" }}>
                  资质与认证
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                  {aboutInfo.licenses.map((license, index) => (
                    <div key={index} className="bg-background-100 rounded-xl p-6 text-center">
                      <div className="w-12 h-12 mx-auto mb-3 bg-accent-50 rounded-full flex items-center justify-center">
                        <i className="ri-shield-check-line text-xl text-accent-500"></i>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">{license.name}</h3>
                      <p className="text-xs text-foreground-400">{license.code}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div id="contact">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8 text-center" style={{ fontFamily: "var(--font-heading)" }}>
                  联系我们
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 max-w-3xl mx-auto">
                  <div className="bg-background-100 rounded-xl p-6 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 bg-primary-50 rounded-full flex items-center justify-center">
                      <i className="ri-mail-line text-xl text-primary-500"></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮件</h3>
                    <p className="text-xs text-foreground-500">{siteInfo.email}</p>
                  </div>
                  <div className="bg-background-100 rounded-xl p-6 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 bg-primary-50 rounded-full flex items-center justify-center">
                      <i className="ri-phone-line text-xl text-primary-500"></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">客服热线</h3>
                    <p className="text-xs text-foreground-500">{siteInfo.phone}</p>
                  </div>
                  <div className="bg-background-100 rounded-xl p-6 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 bg-primary-50 rounded-full flex items-center justify-center">
                      <i className="ri-map-pin-line text-xl text-primary-500"></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h3>
                    <p className="text-xs text-foreground-500">{siteInfo.address}</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}